import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  DialogContentText,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { rolesAPI, departmentsAPI, companiesAPI, permissionsAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { useAuth } from '../../contexts/AuthContext';
import { AutoCloseAlert } from '../Common';

const CustomToolbar = ({ onAdd, hasCreatePermission, hasExportPermission }) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    {hasExportPermission && (
      <GridToolbarExport 
        printOptions={{ disableToolbarButton: true }}
        csvOptions={{ 
          disableToolbarButton: false,
          fileName: 'Roles_Management' // Custom filename for CSV export
        }}
      />
    )}
    {hasCreatePermission && (
      <Button startIcon={<AddIcon />} onClick={onAdd}>
        Add Role
      </Button>
    )}
  </GridToolbarContainer>
);

const Roles = () => {
  const { hasPermission } = usePermissions();
  const { user: currentUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]); // State to store companies for dropdown
  const [filteredDepartments, setFilteredDepartments] = useState([]); // State to store filtered departments based on selected company
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false); // Track if current user is superadmin
  const [formData, setFormData] = useState({
    role_name: '',
    description: '',
    department_id: '',
    company_id: '', // Company ID for role association
    is_active: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  
  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const canView = hasPermission('Roles', 'view');
  const canCreate = hasPermission('Roles', 'create');
  const canUpdate = hasPermission('Roles', 'update');
  const canDelete = hasPermission('Roles', 'delete');
  const canExport = hasPermission('Roles', 'export');

  // Validation functions
  const validateForm = (data) => {
    const errors = {};

    // Role name validation
    if (!data.role_name || !data.role_name.trim()) {
      errors.role_name = 'Role name is required';
    } else if (data.role_name.trim().length < 2) {
      errors.role_name = 'Role name must be at least 2 characters long';
    } else if (data.role_name.trim().length > 100) {
      errors.role_name = 'Role name must be less than 100 characters';
    } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(data.role_name.trim())) {
      errors.role_name = 'Role name can only contain letters, numbers, spaces, hyphens, and underscores';
    }

    // Department validation
    if (!data.department_id) {
      errors.department_id = 'Department is required';
    }

    // Company validation (for superadmin)
    if (isSuperAdmin && !data.company_id) {
      errors.company_id = 'Company is required';
    }

    // Description validation (optional but if provided, validate length)
    if (data.description && data.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }

    return errors;
  };

  const validateField = (fieldName, value) => {
    const errors = {};

    switch (fieldName) {
      case 'role_name':
        if (!value || !value.trim()) {
          errors.role_name = 'Role name is required';
        } else if (value.trim().length < 2) {
          errors.role_name = 'Role name must be at least 2 characters long';
        } else if (value.trim().length > 100) {
          errors.role_name = 'Role name must be less than 100 characters';
        } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(value.trim())) {
          errors.role_name = 'Role name can only contain letters, numbers, spaces, hyphens, and underscores';
        }
        break;
      case 'department_id':
        if (!value) {
          errors.department_id = 'Department is required';
        }
        break;
      case 'company_id':
        if (isSuperAdmin && !value) {
          errors.company_id = 'Company is required';
        }
        break;
      case 'description':
        if (value && value.length > 500) {
          errors.description = 'Description must be less than 500 characters';
        }
        break;
      default:
        break;
    }

    return errors;
  };

  const handleFieldChange = (fieldName, value) => {
    // Update form data
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    // Clear previous error for this field
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });

    // Validate the field
    const fieldErrors = validateField(fieldName, value);
    if (Object.keys(fieldErrors).length > 0) {
      setFormErrors(prev => ({ ...prev, ...fieldErrors }));
    }
  };

  // Add function to load companies for dropdown
  const loadCompanies = async () => {
    try {
      // Fetch all active companies for dropdown selection
      const response = await companiesAPI.getDropdown();
      setCompanies(response.data.data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
      setError('Failed to load companies for dropdown');
    }
  };

  // Add function to check if current user is superadmin
  const checkSuperAdminStatus = async () => {
    try {
      // Check if current user has Super Admin role to enable cross-company role management
      const response = await permissionsAPI.checkSuperAdmin();
      setIsSuperAdmin(response.data.is_superadmin || false);
    } catch (error) {
      console.error('Error checking superadmin status:', error);
      setIsSuperAdmin(false);
    }
  };

  useEffect(() => {
    if (canView) {
      loadRoles();
      loadDepartments();
      loadCompanies(); // Load companies for dropdown
      checkSuperAdminStatus(); // Check if current user is superadmin
    }
  }, [canView, selectedDepartment, paginationModel]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const params = {
        page: paginationModel.page + 1, // Backend expects 1-based page numbers
        per_page: paginationModel.pageSize,
        ...(selectedDepartment && { department_id: selectedDepartment })
      };
      const response = await rolesAPI.getAll(params);
      setRoles(response.data.data || []);
      setRowCount(response.data.total || 0);
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await departmentsAPI.getAll();
      setDepartments(response.data.data || []);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const handleAdd = () => {
    setEditingRole(null);
    setViewMode(false);
    setError('');
    setFormErrors({});
    setIsSubmitting(false);
    setFormData({
      role_name: '',
      description: '',
      department_id: '',
      company_id: isSuperAdmin ? '' : undefined, // Only set company_id for superadmin users
      is_active: true,
    });
    
    // For non-superadmin users, automatically load departments from their company
    if (!isSuperAdmin && currentUser) {
      // Get current user's company from auth context
      const currentUserCompany = currentUser.company_id;
      if (currentUserCompany) {
        // Filter departments to only show those belonging to the current user's company
        const filtered = departments.filter(dept => dept.company_id === currentUserCompany);
        setFilteredDepartments(filtered);
      }
    } else {
      setFilteredDepartments([]); // Reset filtered departments for superadmin
    }
    setDialogOpen(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setViewMode(false);
    setError('');
    setFormErrors({});
    setIsSubmitting(false);
    setFormData({
      role_name: role.role_name,
      description: role.description || '',
      department_id: role.department_id || '',
      company_id: isSuperAdmin ? (role.company_id || '') : undefined, // Only set company_id for superadmin users
      is_active: role.is_active,
    });
    // Filter departments based on role's company
    if (role.company_id) {
      const filtered = departments.filter(dept => dept.company_id === role.company_id);
      setFilteredDepartments(filtered);
    } else {
      setFilteredDepartments([]);
    }
    setDialogOpen(true);
  };

  const handleView = (role) => {
    setEditingRole(role);
    setViewMode(true);
    setError('');
    setFormErrors({});
    setIsSubmitting(false);
    setFormData({
      role_name: role.role_name,
      description: role.description || '',
      department_id: role.department_id || '',
      company_id: isSuperAdmin ? (role.company_id || '') : undefined, // Only set company_id for superadmin users
      is_active: role.is_active,
    });
    // Filter departments based on role's company
    if (role.company_id) {
      const filtered = departments.filter(dept => dept.company_id === role.company_id);
      setFilteredDepartments(filtered);
    } else {
      setFilteredDepartments([]);
    }
    setDialogOpen(true);
  };

  const handleDelete = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (roleToDelete) {
      try {
        await rolesAPI.delete(roleToDelete.id);
        setSuccess('Role deleted successfully');
        setDeleteDialogOpen(false);
        setRoleToDelete(null);
        
        // Check if we need to go back to previous page if current page becomes empty
        const currentPageStartIndex = paginationModel.page * paginationModel.pageSize;
        const totalItemsAfterDelete = rowCount - 1;
        
        if (currentPageStartIndex >= totalItemsAfterDelete && paginationModel.page > 0) {
          setPaginationModel(prev => ({ ...prev, page: prev.page - 1 }));
        } else {
        loadRoles();
        }
      } catch (error) {
        setError('Failed to delete role');
        setDeleteDialogOpen(false);
        setRoleToDelete(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    // Validate the entire form
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      if (editingRole) {
        await rolesAPI.update(editingRole.id, formData);
        setSuccess('Role updated successfully');
        setDialogOpen(false);
        setFormErrors({});
        loadRoles(); // Reload current page
      } else {
        await rolesAPI.create(formData);
        setSuccess('Role created successfully');
        setDialogOpen(false);
        setFormErrors({});
        
        // For new roles, go to first page to see the new item
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      }
    } catch (error) {
      const errorMessage = (error.response && error.response.data && error.response.data.error) || 'Operation failed';
      setError(errorMessage);
      console.error('Role operation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError('');
    setFormErrors({});
    setIsSubmitting(false);
    setEditingRole(null);
    setViewMode(false);
    setFormData({
      role_name: '',
      description: '',
      department_id: '',
      company_id: '',
      is_active: true,
    });
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'role_name', headerName: 'Role Name', width: 200 },
    { field: 'description', headerName: 'Description', width: 250 },
    { 
      field: 'department_name', 
      headerName: 'Department', 
      width: 200,
      renderCell: (params) => (
        <Chip 
          icon={<BusinessIcon />}
          label={params.value}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: 'company_name',
      headerName: 'Company',
      width: 200,
      renderCell: (params) => (
        <Chip 
          label={params.value}
          variant="outlined"
          size="small"
          color="primary"
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      type: 'string',
      valueGetter: (params) => {
        // Return "Active" or "Inactive" text for filtering instead of boolean
        return params.value ? 'Active' : 'Inactive';
      },
      renderCell: (params) => {
        // Use the raw row data to get the actual boolean value
        const isActive = params.row.is_active;
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      type: 'string',
      valueGetter: (params) => {
        if (!params.value) return 'N/A';
        const date = new Date(params.value);
        if (isNaN(date.getTime())) return 'N/A';
        
        // Format date with leading zeros to show DD/MM/YYYY
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
      },
      sortComparator: (v1, v2) => {
        if (v1 === 'N/A' && v2 === 'N/A') return 0;
        if (v1 === 'N/A') return -1;
        if (v2 === 'N/A') return 1;
        
        // Convert DD/MM/YYYY back to Date objects for comparison
        const [day1, month1, year1] = v1.split('/').map(Number);
        const [day2, month2, year2] = v2.split('/').map(Number);
        
        const date1 = new Date(year1, month1 - 1, day1);
        const date2 = new Date(year2, month2 - 1, day2);
        
        return date1.getTime() - date2.getTime();
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => {
        const actions = [];

        if (canView) {
          actions.push(
            <Tooltip title="View Role Details" arrow>
            <GridActionsCellItem
              icon={<ViewIcon />}
              label="View"
              onClick={() => handleView(params.row)}
            />
            </Tooltip>
          );
        }
        
        if (canUpdate) {
          actions.push(
            <Tooltip title="Edit Role" arrow>
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={() => handleEdit(params.row)}
            />
            </Tooltip>
          );
        }
        
        if (canDelete) {
          actions.push(
            <Tooltip title="Delete Role" arrow>
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => handleDelete(params.row)}
            />
            </Tooltip>
          );
        }
        
        return actions;
      },
    },
  ];

  if (!canView) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to view roles.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Roles Management
      </Typography>

      <AutoCloseAlert
        severity="error"
        message={error}
        show={!!error}
        onClose={() => setError('')}
        autoCloseDelay={6000}
      />

      <AutoCloseAlert
        severity="success"
        message={success}
        show={!!success}
        onClose={() => setSuccess('')}
        autoCloseDelay={4000}
      />

      {/* Department Filter */}
      <Box sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="department-filter-label">Filter by Department</InputLabel>
          <Select
            labelId="department-filter-label"
            value={selectedDepartment}
            onChange={(e) => {
              setSelectedDepartment(e.target.value);
              // Reset to first page when filter changes
              setPaginationModel(prev => ({ ...prev, page: 0 }));
            }}
            label="Filter by Department"
          >
            <MenuItem value="">All Departments</MenuItem>
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.department_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper sx={{ height: 'auto', width: '100%' }}>
        <DataGrid
          rows={roles}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          checkboxSelection
          disableSelectionOnClick
          components={{
            Toolbar: CustomToolbar,
          }}
          componentsProps={{
            toolbar: {
              onAdd: handleAdd,
              hasCreatePermission: canCreate,
              hasExportPermission: canExport,
            },
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {viewMode ? 'View Role' : editingRole ? 'Edit Role' : 'Add New Role'}
        </DialogTitle>
        <DialogContent>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Role Name"
              value={formData.role_name}
              onChange={(e) => handleFieldChange('role_name', e.target.value)}
              margin="normal"
              required
              disabled={viewMode}
              error={!!formErrors.role_name}
              helperText={formErrors.role_name || 'Enter a unique role name (2-100 characters)'}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              margin="normal"
              multiline
              rows={3}
              disabled={viewMode}
              error={!!formErrors.description}
              helperText={formErrors.description || 'Optional description (max 500 characters)'}
              inputProps={{ maxLength: 500 }}
            />
            {/* Only show company dropdown for superadmin users */}
            {isSuperAdmin && (
              <FormControl fullWidth margin="normal" error={!!formErrors.company_id}>
                <InputLabel>
                  Company
                </InputLabel>
                <Select
                  value={formData.company_id}
                  onChange={(e) => {
                    handleFieldChange('company_id', e.target.value);
                    setFormData({ 
                      ...formData, 
                      company_id: e.target.value,
                      department_id: '' // Reset department when company changes
                    });
                    // Filter departments based on selected company
                    const selectedCompany = companies.find(c => c.id === e.target.value);
                    if (selectedCompany) {
                      const filtered = departments.filter(dept => dept.company_id === selectedCompany.id);
                      setFilteredDepartments(filtered);
                    } else {
                      setFilteredDepartments([]);
                    }
                  }}
                  label="Company"
                  required
                  disabled={viewMode}
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.company_name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.company_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {formErrors.company_id}
                  </Typography>
                )}
              </FormControl>
            )}

            <FormControl fullWidth margin="normal" error={!!formErrors.department_id}>
              <InputLabel>Department</InputLabel>
              <Select
                value={formData.department_id}
                onChange={(e) => handleFieldChange('department_id', e.target.value)}
                label="Department"
                required
                disabled={viewMode}
              >
                {/* For non-superadmin users, show all departments if filtered list is empty */}
                {/* This ensures departments are always available even if filtering fails */}
                {(filteredDepartments.length > 0 ? filteredDepartments : departments).map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.department_name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.department_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {formErrors.department_id}
                </Typography>
              )}
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Active Status"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {viewMode ? 'Close' : 'Cancel'}
          </Button>
          {!viewMode && (
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              disabled={isSubmitting || Object.keys(formErrors).length > 0}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting 
                ? (editingRole ? 'Updating...' : 'Creating...') 
                : (editingRole ? 'Update' : 'Create')
              }
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DeleteIcon />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Are you sure you want to delete the role{' '}
            <strong>{roleToDelete?.role_name}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
            This action cannot be undone. The role will be permanently removed from the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCancelDelete}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 100 }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Roles; 